import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { getBooleanValue } from '@experiences/util';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApBadge,
    ApCheckbox,
    ApIconButton,
    ApRadioButton,
    ApRadioGroup,
    ApTooltip,
} from '@uipath/portal-shell-react';
import React from 'react';
import type {
    Control,
    FieldPath,
    FieldPathValue,
    SetValueConfig,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useIntl } from 'react-intl';

import type { ByoLlmConfiguration } from '../../../../services/llm-gateway/LlmGateway.types';
import type { AddEditConfigurationFormData } from '../AddEditConfigurationViewModel';

const useStyles = makeStyles(_ =>
    createStyles({
        tooltipIcon: {
            width: '20px',
            height: '20px',
            marginLeft: '6px',
        },
        previewBadge: { marginLeft: '6px' },
    }),
);

type AddEditConfigurationOptionsSectionProps = {
    control: Control<AddEditConfigurationFormData>;
    resetField: (name: FieldPath<AddEditConfigurationFormData>) => void;
    setValue:
    (name: FieldPath<AddEditConfigurationFormData>, value: FieldPathValue<AddEditConfigurationFormData, FieldPath<AddEditConfigurationFormData>>, options?: SetValueConfig) => void;
    configurationMode?: string;
    feature?: string;
    featureHasAllModelsConfigurationConstraint: boolean;
    featureSupportsByoModel: boolean;
    canUseByoAiGateway: boolean;
    notifyFieldChanges: () => void;
    replaceableLlms: string[];
    disabled?: boolean;
};

export const AddEditConfigurationOptionsSection: React.FC<AddEditConfigurationOptionsSectionProps> = ({
    control,
    resetField,
    setValue,
    configurationMode,
    feature,
    featureHasAllModelsConfigurationConstraint,
    featureSupportsByoModel,
    canUseByoAiGateway,
    notifyFieldChanges,
    replaceableLlms,
    disabled,
}: AddEditConfigurationOptionsSectionProps) => {
    const { formatMessage: translate } = useIntl();
    const classes = useStyles();

    return (<UiStack
        direction='row'
        mt={SpacingToken.S}
        gap={SpacingToken.XXL}
        align='center'
    >
        <UiStack
            direction='column'
            gap={SpacingToken.M}
        >
            <UiStack
                direction='row'
                align='center'
            >
                <UiText variant={FontVariantToken.fontSizeLBold}>
                    {translate({ id: 'CLIENT_BYO_LLM_CONFIGURATION_MODE' })}
                </UiText>
                <ApTooltip content={translate({ id: 'CLIENT_BYO_LLM_CONFIGURATION_MODE_TOOLTIP' })}>
                    <ApIconButton
                        data-cy='byo-llm-configuration-mode-tooltip'
                    >
                        <InfoOutlinedIcon
                            className={classes.tooltipIcon}
                            color='action'
                            aria-label={translate({ id: 'CLIENT_BYO_LLM_CONFIGURATION_MODE_TOOLTIP' })}
                        />
                    </ApIconButton>
                </ApTooltip>
            </UiStack>
            {canUseByoAiGateway && (
                <UiStack
                    direction='row'
                    align='center'
                >
                    <UiText variant={FontVariantToken.fontSizeLBold}>
                        {translate({ id: 'CLIENT_BYO_LLM_USE_AI_GATEWAY' })}
                    </UiText>
                    <ApTooltip content={translate({ id: 'CLIENT_BYO_LLM_USE_AI_GATEWAY_TOOLTIP' })}>
                        <ApIconButton
                            data-cy='byo-llm-use-ai-gateway-tooltip'
                        >
                            <InfoOutlinedIcon
                                className={classes.tooltipIcon}
                                color='action'
                                aria-label={translate({ id: 'CLIENT_BYO_LLM_USE_AI_GATEWAY_TOOLTIP' })} />
                        </ApIconButton>
                    </ApTooltip>
                    <ApBadge
                        className={classes.previewBadge}
                        label={translate({ id: 'CLIENT_PREVIEW' })}
                        status="info"
                    />
                </UiStack>)}
        </UiStack>
        <UiStack
            direction='column'
            gap={SpacingToken.XS}
        >
            <Controller
                name="configurationMode"
                control={control}
                rules={{ required: translate({ id: 'CLIENT_REQUIRED_FIELD_ERROR' }) }}
                render={({ field }) => (
                    <ApRadioGroup
                        value={field.value}
                        onValueChanged={(event) => {
                            field.onChange(event.detail);

                            if (event.detail === 'replace') {
                                resetField('llmConfigurations');
                            } else {
                                // Add entry for empty alias
                                setValue('llmConfigurations', { '': {} });
                            }

                            notifyFieldChanges();
                        }}
                        data-cy="configuration-mode-radio-group"
                        disabled={!!disabled || !feature}
                        orientation="horizontal"
                    >
                        <ApRadioButton
                            value="replace"
                            label={translate({ id: 'CLIENT_BYO_REPLACE_UIPATH_MODEL' })}
                        />
                        {featureSupportsByoModel && (
                            <ApRadioButton
                                value="add"
                                label={translate({ id: 'CLIENT_BYO_ADD_OWN_MODEL' })}
                            />)}
                    </ApRadioGroup>
                )}
            />
            {canUseByoAiGateway && (
                <Controller
                    name="useAiGateway"
                    control={control}
                    render={({ field }) => (
                        <ApCheckbox
                            style={{ paddingLeft: '1em' }}
                            checked={field.value}
                            disabled={!!disabled || !feature}
                            onValueChanged={(event) => {
                                field.onChange(getBooleanValue(event.detail));

                                if (featureHasAllModelsConfigurationConstraint) {
                                    setValue('llmConfigurations', replaceableLlms
                                        .reduce((acc, llm) => {
                                            acc[llm] = {
                                                desiredModel: llm,
                                                defaultModel: llm,
                                            };
                                            return acc;
                                        }, {} as Record<string, Partial<ByoLlmConfiguration>>) ?? {});
                                } else if (configurationMode === 'replace') {
                                    resetField('llmConfigurations');
                                } else {
                                    // Add entry for empty alias
                                    setValue('llmConfigurations', { '': {} });
                                }

                                notifyFieldChanges();
                            }}
                            data-cy="use-ai-gateway-checkbox"
                        />
                    )}
                />)}
        </UiStack>
    </UiStack>);
};
